import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertColor,
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Snackbar,
  Tab,
  Tabs,
} from '@mui/material';
import BackLink from '../../BackLink';
import TabPanel from '../../TabPanel';
import EntityTitle from '../../EntityTitle';
import PersonIcon from '@mui/icons-material/Person';
import * as providerSlice from '../../../data/provider.slice';
import * as driverSlice from '../../../data/driver.slice';
import ContactInfo from './ContactInfo';
import EmergencyContactInfo from './EmergencyContactInfo';
import TransportationInfo from './TransportationInfo';
import DriverDialog from './DriverDialog';
import phoneUtils from '../../../services/phoneUtils';
import DriverDocuments from './DriverDocuments';
import Entity, { entityClasses } from '../Entity';
import { useCanEditDriver } from '../../../hooks/permissions/useCanEditDriver';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import { useSearchParams } from 'react-router-dom';

export default function Driver() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const canEditDriver = useCanEditDriver();
  const isProviderUser = !!useLoggedInProviderId();

  const params = useParams<{ id: string }>();

  const [driverTab, setDriverTab] = React.useState(0);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState<string>();
  const [snackbarSeverity, setSnackbarSeverity] =
    React.useState<AlertColor>('error');
  const [driverLabel, setDriverLabel] = React.useState('');

  const loadingDriver = useAppSelector(driverSlice.selectLoadingDriver);
  const loadedDriver = useAppSelector(driverSlice.selectLoadedDriver);
  const driver = useAppSelector(driverSlice.selectDriver);
  const uploadingDocument = useAppSelector(driverSlice.selectUploadingDocument);
  const uploadError = useAppSelector(driverSlice.selectUploadError);

  React.useEffect(() => {
    if (uploadError) {
      setSnackbarMessage(t(uploadError));
      setSnackbarSeverity('error');
    }
  }, [uploadError]);

  React.useEffect(() => {
    const id = parseInt(params.id!, 10);
    dispatch(driverSlice.getDriverAsync(id));
  }, [params, dispatch]);

  const provider = useAppSelector(providerSlice.selectProvider);

  React.useEffect(() => {
    if (driver?.transportationProviderID) {
      dispatch(providerSlice.getProviderAsync(driver.transportationProviderID));
    }
  }, [driver, dispatch]);

  React.useMemo(() => {
    setDriverLabel(
      driver
        ? `${driver?.driverFirstName} ${driver?.driverLastName}`
        : t('driver.unknownDriver'),
    );
  }, [driver]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setDriverTab(newValue);
  };

  const editDriver = () => {
    setEditorOpen(true);
  };

  const closeDriverEditor = (saved: boolean) => {
    if (searchParams.get('edit')) {
      dispatch(driverSlice.resetSavingFlags());

      navigate(-1);
      return;
    }
    if (saved) {
      const id = parseInt(params.id!, 10);
      dispatch(driverSlice.getDriverAsync(id));
    }
    setEditorOpen(false);
    dispatch(driverSlice.resetSavingFlags());
  };

  const onSnackbarClose = () => {
    setSnackbarMessage(undefined);
    dispatch(driverSlice.setUploadError(null));
  };

  return (
    <Entity>
      <BackLink />

      <Box component="div" className={entityClasses.content}>
        <Box component="div" className={entityClasses.left}>
          <Card className={entityClasses.card}>
            <CardHeader
              title={
                <EntityTitle
                  icon={<PersonIcon />}
                  label={driverLabel}
                  loading={loadingDriver}
                  readOnly={!canEditDriver || !loadedDriver}
                  onEdit={editDriver}
                />
              }
            />
            {driver && (
              <CardContent>
                <ContactInfo
                  driver={driver}
                  titleStyle={entityClasses.section}
                />
              </CardContent>
            )}
          </Card>

          {driver && (
            <Card className={entityClasses.card}>
              <CardContent>
                <EmergencyContactInfo
                  phone={phoneUtils.getDriverEmergencyPhone(
                    driver?.phoneNumbers,
                  )}
                  titleStyle={entityClasses.section}
                />
              </CardContent>
            </Card>
          )}
        </Box>
        {driver && (
          <Box component="div" className={entityClasses.right}>
            <Paper className={entityClasses.tabs}>
              <Tabs
                indicatorColor="primary"
                textColor="primary"
                value={driverTab}
                onChange={handleTabChange}
                variant="fullWidth">
                <Tab label={t('driver.info')} />
                <Tab label={t('common.documents')} />
              </Tabs>
              <TabPanel value={driverTab} index={0}>
                <TransportationInfo driver={driver} />
              </TabPanel>
              <TabPanel value={driverTab} index={1}>
                <DriverDocuments
                  driver={driver}
                  uploadingDocument={uploadingDocument}
                  readOnly={!canEditDriver}
                  allowView={!isProviderUser}
                />
              </TabPanel>
            </Paper>
          </Box>
        )}
      </Box>

      {driver ? (
        <DriverDialog
          title={t('drivers.edit')}
          open={editorOpen}
          driver={driver}
          provider={provider!}
          close={closeDriverEditor}
        />
      ) : null}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Entity>
  );
}
